import React, { useState, useEffect, useRef } from 'react';
import { Ico } from './icons';
import { TiptapEditor } from './tiptap_editor';
import { TabEditPopover } from './tab_edit_popover';
import { showToast } from '../lib/toast';

// A tab without a color is an empty placeholder slot — it renders as a
// muted gray cell (showing the label, if any, in dimmed text) and is not
// clickable in preview mode. Picking a color makes the tab "real".
const isEmptyTab = (tab) => !tab.label;

// Page — with inside-the-page tab rails (multiple stacked rails per side)
export function Page({ doc, page, documentId, onTabClick, onTabDoubleClick, onTabDrop, dragTabId, setDragTabId,
                isEditing, onEditContent, onEditContent2, onEditImage, currentTabId,
                onUpdateTab, onDeleteTab, onAddTabToRail, onSetRailCount, onRemoveRail, onUpdatePage,
                onEditorReady,
                onInsertInlineTab, onOpenPage, onRenameInlineTab, onUpdateInlineTab, onDeleteInlineTab, onClonePageButtonSubtree, onRestoreInlineSubtree }) {
  const tabs = doc.tabs;
  const [editTab, setEditTab] = useState(null); // { tabId, rect, loc }
  const imgInputRef = useRef(null);

  useEffect(() => {
    if (!isEditing) setEditTab(null);
  }, [isEditing]);

  // Resolve once: drives both the render guard below and avoids a transient
  // `tab={undefined}` window in TabEditPopover when the tab is deleted out
  // from under us (undo/redo, keyboard shortcut, etc.).
  const editingTab = (isEditing && editTab) ? tabs.find(t => t.id === editTab.tabId) : null;

  // Group tabs by location, then by rail index (default 0)
  // Inline tabs (referenced from a pageButton inside content) are excluded — they have no rail position.
  const byLoc = { left:{}, right:{}, top:{}, bottom:{} };
  tabs.forEach(t => {
    if (t.inline) return;
    const loc = t.location || 'left';
    const rail = t.rail || 0;
    if (!byLoc[loc][rail]) byLoc[loc][rail] = [];
    byLoc[loc][rail].push(t);
  });
  // For each side, the count of rails = max(rail index used + 1, configured count, 1 if has any tab on that side)
  const railCountFor = (loc) => {
    const explicit = doc.rails && doc.rails[loc];
    const usedRails = Object.keys(byLoc[loc]).map(Number);
    const maxUsed = usedRails.length ? Math.max(...usedRails) + 1 : 0;
    return Math.max(explicit || 0, maxUsed);
  };

  const railsOnSide = (loc) => {
    const count = railCountFor(loc);
    return Array.from({length: count}, (_, i) => i);
  };

  // Tab size is configured per side (not per rail) — every rail on a given
  // side uses the same size so chips line up with their neighbors visually.
  const railSizeFor = (loc) => (doc.railSizes && doc.railSizes[loc]) || 'small';

  const railHasTabs = (loc, railIdx) =>
    tabs.some(t => (t.location||'left')===loc && (t.rail||0)===railIdx);

  const handleChipClick = (tab, e) => {
    e.preventDefault();
    if (isEditing) {
      const rect = e.currentTarget.getBoundingClientRect();
      setEditTab({ tabId: tab.id, rect, loc: tab.location || 'left' });
    } else if (!isEmptyTab(tab)) {
      onTabClick(tab.id);
    }
  };

  const renderSide = (loc) => {
    const rails = railsOnSide(loc);
    // In edit mode, even empty sides get an "Add rail" affordance, so we still render the side container.
    if (rails.length === 0 && !isEditing) return <div className={`side side-${loc} side-empty`}/>;
    return (
      <div className={`side side-${loc} ${isEditing?'side-editing':''}`}>
        {rails.map(railIdx => {
          const list = byLoc[loc][railIdx] || [];
          const rs = railSizeFor(loc);
          const rsClass = {small:'rs-s',medium:'rs-m',large:'rs-l'}[rs] || 'rs-s';
          const railLetter = loc==='top'?'t':loc==='bottom'?'b':loc==='left'?'l':'r';
          const isEmpty = list.length === 0;
          return (
            <div key={railIdx}
                 className={`tab-rail rail-${railLetter} ${rsClass} ${isEmpty?'tab-rail-empty':''}`}
                 onDragOver={(e)=>{e.preventDefault();}}>
              {list.map(t => (
                  <TabChip key={t.id} tab={t}
                    current={t.id === currentTabId}
                    dragging={dragTabId === t.id}
                    isEditing={isEditing}
                    onClick={(e)=>handleChipClick(t, e)}
                    onDoubleClick={()=> isEditing ? onTabClick(t.id) : onTabDoubleClick(t.id)}
                    onDragStart={()=>setDragTabId(t.id)}
                    onDragEnd={()=>setDragTabId(null)}
                    onDrop={(e)=>{e.preventDefault(); onTabDrop(t.id, loc, railIdx);}}
                    onDragOver={(e)=>e.preventDefault()}/>
              ))}
              {isEmpty && !isEditing && <div className="tab-rail-placeholder">Empty rail</div>}
              {isEditing && (
                <button className={`tab-chip-add rail-${railLetter}`}
                        title="Add tab to this rail"
                        onClick={(e)=>{
                          e.stopPropagation();
                          const rect = e.currentTarget.getBoundingClientRect();
                          const id = onAddTabToRail(loc, railIdx);
                          if (id) setEditTab({ tabId:id, rect, loc });
                        }}>
                  <Ico.Plus width="14" height="14"/>
                </button>
              )}
              {isEditing && isEmpty && (
                <button className={`tab-rail-remove rail-${railLetter}`}
                        title="Remove this rail"
                        onClick={(e)=>{ e.stopPropagation(); onRemoveRail(loc, railIdx); }}>
                  <Ico.Trash width="12" height="12"/>
                </button>
              )}
            </div>
          );
        })}
        {isEditing && (
          <button className={`tab-rail-add rail-${loc==='top'?'t':loc==='bottom'?'b':loc==='left'?'l':'r'}`}
                  title="Add a new rail on this side"
                  onClick={()=>onSetRailCount(loc, rails.length + 1)}>
            <Ico.Plus width="12" height="12"/>
            {(loc==='top' || loc==='bottom') && <span>Add rail</span>}
          </button>
        )}
      </div>
    );
  };

  const emptyClasses = ['top','bottom','left','right']
    .filter(loc => railsOnSide(loc).length === 0 && !isEditing)
    .map(loc => `no-${loc}`)
    .join(' ');
  const textCls = doc.textSize && doc.textSize !== 'default' ? `text-${doc.textSize}` : '';
  const cls = `page ${doc.orientation||'portrait'} cols-${page.layout==='double'?2:1} ${emptyClasses} ${isEditing?'edit-mode':''} ${textCls}`;

  const handleAddImage = (imgId, dataUrl, caption) => {
    if (!onEditImage) return;
    if (dataUrl === null) {
      const imgs = { ...(page.images || {}) };
      delete imgs[imgId];
      onEditImage(imgs, imgId, caption);
    } else {
      if (!imgId) imgId = 'i' + Math.random().toString(36).slice(2,7);
      const imgs = { ...(page.images || {}), [imgId]: dataUrl };
      onEditImage(imgs, imgId, caption);
    }
  };

  return (
    <div className={cls}>
      {renderSide('top')}
      {renderSide('left')}
      <div className="page-content">
        {isEditing && onUpdatePage && (
          <div className="page-header">
            <div className="ph-right">
              <div className="seg-control seg-control--sm">
                <button className={`seg-control__btn ${(page.layout||'single')==='single'?'seg-control__btn--active':''}`}
                        data-tip="One column"
                        onClick={()=>onUpdatePage({layout:'single'})}>
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><rect x="2.5" y="2.5" width="11" height="11" rx="1" stroke="currentColor" strokeWidth="1.2"/></svg>
                </button>
                <button className={`seg-control__btn ${page.layout==='double'?'seg-control__btn--active':''}`}
                        data-tip="Two columns"
                        onClick={()=>onUpdatePage({layout:'double'})}>
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><rect x="2.5" y="2.5" width="11" height="11" rx="1" stroke="currentColor" strokeWidth="1.2"/><line x1="8" y1="3" x2="8" y2="13" stroke="currentColor" strokeWidth="1"/></svg>
                </button>
              </div>
            </div>
          </div>
        )}
        {page.layout === 'double' ? (
          isEditing ? (
            <>
              <div className="col-edit">
                <TiptapEditor content={page.content} onChange={onEditContent} onEditorReady={onEditorReady}
                              doc={doc} documentId={documentId}
                              onInsertInlineTab={onInsertInlineTab}
                              onOpenPage={onOpenPage}
                              onRenameInlineTab={onRenameInlineTab}
                              onUpdateInlineTab={onUpdateInlineTab}
                              onDeleteInlineTab={onDeleteInlineTab}
                              onClonePageButtonSubtree={onClonePageButtonSubtree}
                              onRestoreInlineSubtree={onRestoreInlineSubtree}/>
              </div>
              <div className="col-edit">
                <TiptapEditor content={page.content2} onChange={onEditContent2} onEditorReady={onEditorReady}
                              doc={doc} documentId={documentId}
                              onInsertInlineTab={onInsertInlineTab}
                              onOpenPage={onOpenPage}
                              onRenameInlineTab={onRenameInlineTab}
                              onUpdateInlineTab={onUpdateInlineTab}
                              onDeleteInlineTab={onDeleteInlineTab}
                              onClonePageButtonSubtree={onClonePageButtonSubtree}
                              onRestoreInlineSubtree={onRestoreInlineSubtree}/>
              </div>
            </>
          ) : (
            <>
              <div className="col-view">
                <TiptapEditor content={page.content} editable={false}
                              doc={doc}
                              onOpenPage={onOpenPage}/>
              </div>
              <div className="col-view">
                <TiptapEditor content={page.content2} editable={false}
                              doc={doc}
                              onOpenPage={onOpenPage}/>
              </div>
            </>
          )
        ) : isEditing ? (
          <TiptapEditor content={page.content} onChange={onEditContent} onEditorReady={onEditorReady}
                        doc={doc} documentId={documentId}
                        onInsertInlineTab={onInsertInlineTab}
                        onOpenPage={onOpenPage}
                        onRenameInlineTab={onRenameInlineTab}
                        onUpdateInlineTab={onUpdateInlineTab}
                        onDeleteInlineTab={onDeleteInlineTab}
                        onClonePageButtonSubtree={onClonePageButtonSubtree}
                        onRestoreInlineSubtree={onRestoreInlineSubtree}/>
        ) : (
          <TiptapEditor content={page.content} editable={false}
                        doc={doc}
                        onOpenPage={onOpenPage}/>
        )}
      </div>
      {renderSide('right')}
      {renderSide('bottom')}

      {editingTab && (
        <TabEditPopover
          tab={editingTab}
          rect={editTab.rect}
          loc={editTab.loc}
          onClose={()=>setEditTab(null)}
          onUpdate={(patch)=>onUpdateTab(editTab.tabId, patch)}
          onDelete={()=>{ onDeleteTab(editTab.tabId); setEditTab(null); showToast('Tab deleted · Ctrl+Z to undo'); }}/>
      )}
    </div>
  );
}

function TabChip({ tab, current, dragging, isEditing, onClick, onDoubleClick,
                   onDragStart, onDragEnd, onDrop, onDragOver }) {
  const empty = isEmptyTab(tab);
  const colorCls = tab.color ? `c-${tab.color}` : 'c-none';
  const emptyCls = empty ? 'empty' : '';
  // In edit mode, render as a div (so the popover can be its own button without nesting button-in-button)
  if (isEditing) {
    return (
      <div className={`tab-chip ${colorCls} ${emptyCls} ${current?'current':''} editable`}
           role="button"
           tabIndex={0}
           draggable="true"
           onClick={onClick}
           onDoubleClick={onDoubleClick}
           onKeyDown={(e)=>{ if (e.key==='Enter' || e.key===' ') { e.preventDefault(); onClick(e); } }}
           onDragStart={onDragStart}
           onDragEnd={onDragEnd}
           onDrop={onDrop}
           onDragOver={onDragOver}>
        {tab.label}
      </div>
    );
  }
  const pageId = tab.pageIds && tab.pageIds[0];
  return (
    <a className={`tab-chip ${colorCls} ${emptyCls} ${current?'current':''} ${dragging?'dragging':''}`}
       href={!empty && pageId ? `#page-${pageId}` : undefined}
       draggable="true"
       tabIndex={empty ? -1 : 0}
       aria-disabled={empty || undefined}
       onClick={onClick}
       onDoubleClick={onDoubleClick}
       onDragStart={onDragStart}
       onDragEnd={onDragEnd}
       onDrop={onDrop}
       onDragOver={onDragOver}>
      {tab.label}
    </a>
  );
}

// Floating popover anchored to a tab chip — lets users rename, recolor, or delete the tab
// without leaving the editor canvas. Positioning adapts to which side the rail sits on.

